import { lazy, type ComponentType } from 'react';
import { Loader2 } from 'lucide-react';

/**
 * Charger une page seulement quand on y va.
 *
 * Toutes les pages importées d'un bloc faisaient un seul fichier de 2,4 Mo que
 * l'accueil devait télécharger en entier : sur une connexion lente, plusieurs
 * minutes, et parfois une coupure en cours de route. Chaque page a désormais
 * son propre fichier.
 *
 * Après un déploiement, les anciens fichiers disparaissent du serveur. Un
 * onglet ouvert avant le déploiement qui navigue vers une page pas encore
 * chargée échoue alors à la télécharger : on recharge une fois, ce qui
 * récupère la nouvelle version. Le drapeau en session évite une boucle si
 * l'échec vient d'ailleurs (réseau coupé).
 */
const CLE_RECHARGEMENT = 'fc-rechargement-version';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyPage<T extends ComponentType<any>>(charger: () => Promise<{ default: T }>) {
  return lazy(() =>
    charger()
      .then((module) => {
        try {
          sessionStorage.removeItem(CLE_RECHARGEMENT);
        } catch {
          /* stockage indisponible : sans conséquence */
        }
        return module;
      })
      .catch((erreur) => {
        let dejaTente = true;
        try {
          dejaTente = sessionStorage.getItem(CLE_RECHARGEMENT) === '1';
          if (!dejaTente) sessionStorage.setItem(CLE_RECHARGEMENT, '1');
        } catch {
          /* sans stockage, on ne recharge pas : pas de boucle possible */
        }
        if (!dejaTente) {
          window.location.reload();
          return new Promise<{ default: T }>(() => undefined);
        }
        throw erreur;
      })
  );
}

/** Ce qu'on voit le temps qu'une page arrive : l'en-tête reste, seul le contenu attend. */
export function ChargementPage() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center" role="status" aria-label="Chargement">
      <Loader2 className="h-8 w-8 animate-spin text-green-600" />
    </div>
  );
}
